<template>
    <div class="d-flex align-items-start w-100">                    
        <!-- Rechts: Erstbeitrag und Jahresbeitrag -->
        <div class="d-flex flex-column align-items-end flex-grow-1 text-end">
            <!-- Hinweis wenn Versicherungsbeginn ungültig -->
            <template v-if="item.tarif6 === 1 && !hatGueltigeVersicherungsbeginn">
                <div class="text-muted">
                    Bitte gültigen Versicherungsbeginn eingeben.
                </div>
            </template>
            <!-- Hinweis wenn Geburtsdatum fehlt -->
            <template v-else-if="item.tarif6 === 1 && !hatGueltigesGeburtsdatum">
                <div class="text-muted">
                    Bitte geben Sie das Geburtsdatum ein.
                </div>
            </template>
            <!-- Erstbeitrag und Jahresbeitrag wenn alles vorhanden -->
            <template v-else-if="item.tarif6 === 1 && mindestlaufzeit.monate > 0 && jahresbeitrag > 0">
                <div class="beitrag-zeile">
                    <span class="beitrag-label">Erstbeitrag ({{ mindestlaufzeit.vonStr }} - {{ mindestlaufzeit.bisStr }}):</span>
                    <strong class="beitrag-wert">{{ erstbeitragStr }}</strong>
                </div>
                <div class="beitrag-zeile">
                    <span class="beitrag-label">Jahresbeitrag (ab {{ jahresbeitragStartStr }}):</span>
                    <strong class="beitrag-wert">{{ jahresbeitragStr }}</strong>
                </div>
            </template>
            <!-- Fallback: nur Jahresbeitrag -->
            <template v-else-if="item.tarif6 === 1 && jahresbeitrag > 0">
                <div>
                    Jahresbeitrag: <strong>{{ jahresbeitragStr }}</strong>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRechnerStore } from '@/stores/rechner';
import { 
    berechneMindestlaufzeit, 
    berechneJahresbeitragStart,
    berechneAR20Beitraege,
    formatiereBeitrag,
    isValidDateInPast,
    isValidVersicherungsbeginnAR20
} from '@/helpers/hilfsfunktionen';

const props = defineProps({
    item: {
        type: Object,
        required: true
    }
});

const store = useRechnerStore();

// Prüft ob ein gültiges Geburtsdatum vorhanden ist
const hatGueltigesGeburtsdatum = computed(() => {
    return isValidDateInPast(props.item.geburtstag);
});

// Prüft ob der Versicherungsbeginn gültig ist (heute bis +6 Monate)
const hatGueltigeVersicherungsbeginn = computed(() => {
    return isValidVersicherungsbeginnAR20(store.versicherungsbeginn);
});

// Mindestlaufzeit basierend auf globalem Versicherungsbeginn (nur wenn gültig)
const mindestlaufzeit = computed(() => {
    if (!hatGueltigeVersicherungsbeginn.value) {
        return { monate: 0, von: null, bis: null, vonStr: '', bisStr: '' };
    }
    return berechneMindestlaufzeit(store.versicherungsbeginn);
});

// Start des regulären Jahresbeitrags (Tag nach Ende Mindestlaufzeit)
const jahresbeitragStartStr = computed(() => {
    if (!hatGueltigeVersicherungsbeginn.value) return '';
    return berechneJahresbeitragStart(store.versicherungsbeginn);
});

// Jahresbeitrag aus dem Store (tarif6beitrag ist der Jahresbeitrag aus der API basierend auf Alter)
const jahresbeitrag = computed(() => {
    return props.item.tarif6beitrag || 0;
});

// Erstbeitrag berechnen
const erstbeitrag = computed(() => {
    if (!hatGueltigeVersicherungsbeginn.value) return 0;
    const beitraege = berechneAR20Beitraege(jahresbeitrag.value, store.versicherungsbeginn);
    return beitraege.erstbeitrag;
});

// Formatierte Strings
const erstbeitragStr = computed(() => {
    return formatiereBeitrag(erstbeitrag.value);
});

const jahresbeitragStr = computed(() => {
    return formatiereBeitrag(jahresbeitrag.value);
});
</script>

<style scoped>
.beitrag-zeile {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-bottom: 0.25rem;
}

.beitrag-label {
    font-size: 0.9rem;
}

.beitrag-wert {
    font-size: 1rem;
}

@media (min-width: 576px) {
    .beitrag-zeile {
        flex-direction: row;
        gap: 0.5rem;
        margin-bottom: 0;
    }
    
    .beitrag-label {
        font-size: 1rem;
    }
}
</style>
